import React from 'react';
import {
  ArrowClockwise,
  ArrowUUpLeft,
  ArrowsOut,
  Clipboard,
  Copy,
  DownloadSimple,
  FloppyDisk,
  GridNine,
  MagnifyingGlassMinus,
  MagnifyingGlassPlus,
  Play,
  Trash,
  UploadSimple
} from '@phosphor-icons/react';

/**
 * FlowToolbar - Top toolbar for flow canvas operations
 * 
 * Features:
 * - Undo/Redo
 * - Delete/Copy/Paste
 * - Zoom controls
 * - Auto-layout
 * - Export/Import
 */

// Tool button component - defined outside main component
const ToolButton = ({ icon: Icon, label, onClick, disabled, variant = 'default', shortcut, readOnly }) => (
  <button
    onClick={onClick}
    disabled={disabled || readOnly}
    title={`${label}${shortcut ? ` (${shortcut})` : ''}`}
    className={`
      p-2 rounded-lg transition-all flex items-center gap-1.5
      ${disabled || readOnly 
        ? 'text-slate-600 cursor-not-allowed' 
        : variant === 'danger'
          ? 'text-red-400 hover:bg-red-500/20 hover:text-red-300'
          : variant === 'primary'
            ? 'text-indigo-400 hover:bg-indigo-500/20 hover:text-indigo-300'
            : 'text-slate-400 hover:bg-white/10 hover:text-white'
      }
    `}
  >
    <Icon size={16} />
  </button>
);

// Divider component - defined outside main component
const ToolDivider = () => (
  <div className="w-px h-5 bg-[var(--border-default)] mx-1" />
);

const FlowToolbar = ({
  onUndo,
  onRedo,
  onDelete,
  onCopy,
  onPaste,
  onZoomIn,
  onZoomOut,
  onFitView,
  onAutoLayout,
  onShowShortcuts,
  onSave,
  onExport,
  onImport,
  onRun,
  canUndo = false,
  canRedo = false,
  hasSelection = false,
  hasClipboard = false,
  readOnly = false
}) => {
  return (
    <div 
      className="absolute top-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1 
        px-2 py-1.5 rounded-[10px] bg-[var(--surface-1)] border border-[var(--border-default)] 
        shadow-lg shadow-black/25"
      data-testid="flow-toolbar"
    >
      {/* History */}
      <div className="flex items-center">
        <ToolButton 
          icon={ArrowUUpLeft} 
          label="Undo" 
          onClick={onUndo} 
          disabled={!canUndo}
          shortcut="Ctrl+Z"
          readOnly={readOnly}
        />
        <ToolButton 
          icon={ArrowClockwise} 
          label="Redo" 
          onClick={onRedo} 
          disabled={!canRedo}
          shortcut="Ctrl+Y"
          readOnly={readOnly}
        />
      </div>

      <ToolDivider />

      {/* Edit */}
      <div className="flex items-center">
        <ToolButton 
          icon={Copy} 
          label="Copy" 
          onClick={onCopy} 
          disabled={!hasSelection}
          shortcut="Ctrl+C"
          readOnly={readOnly}
        />
        <ToolButton 
          icon={Clipboard} 
          label="Paste" 
          onClick={onPaste} 
          disabled={!hasClipboard}
          shortcut="Ctrl+V"
          readOnly={readOnly}
        />
        <ToolButton 
          icon={Trash} 
          label="Delete" 
          onClick={onDelete} 
          disabled={!hasSelection}
          variant="danger"
          shortcut="Del"
          readOnly={readOnly}
        />
      </div>

      <ToolDivider />

      {/* View */}
      <div className="flex items-center">
        <ToolButton 
          icon={MagnifyingGlassMinus} 
          label="Zoom Out" 
          onClick={onZoomOut}
          shortcut="-"
          readOnly={readOnly}
        />
        <ToolButton 
          icon={MagnifyingGlassPlus} 
          label="Zoom In" 
          onClick={onZoomIn}
          shortcut="+"
          readOnly={readOnly}
        />
        <ToolButton 
          icon={ArrowsOut} 
          label="Fit View" 
          onClick={onFitView}
          shortcut="F"
          readOnly={readOnly}
        />
      </div>

      <ToolDivider />

      {/* Layout */}
      <div className="flex items-center">
        <ToolButton 
          icon={GridNine} 
          label="Auto Layout" 
          onClick={onAutoLayout}
          shortcut="L"
          readOnly={readOnly}
        />
      </div>

      <ToolDivider />

      {/* Actions */}
      <div className="flex items-center">
        {onSave && (
          <ToolButton 
            icon={FloppyDisk} 
            label="Save" 
            onClick={onSave}
            variant="primary"
            shortcut="Ctrl+S"
            readOnly={readOnly}
          />
        )}
        {onRun && (
          <ToolButton 
            icon={Play} 
            label="Run Flow" 
            onClick={onRun}
            variant="primary"
            readOnly={readOnly}
          />
        )}
      </div>

      {/* Keyboard Shortcuts Hint */}
      <div className="ml-2 pl-2 border-l border-[var(--border-default)]">
        <button 
          onClick={onShowShortcuts}
          className="text-[10px] text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors cursor-pointer"
          title="Show keyboard shortcuts"
        >
          ? for shortcuts
        </button>
      </div>
    </div>
  );
};

export default FlowToolbar;
